import UserAuthStore from "../../store/userStore";
import { getInitials, timeConverter } from "../../utils/helpers";
import { useNavigate } from "react-router-dom";
import { Globe } from "lucide-react";

import SpectraUsersSharedStack from "../../components/Spectra/SpectraUserSharedStack";


import Header from "../../components/Header/Header";
import ROUTES from "../../router/routes";
import UIStore from "../../store/uiStore";
import { useGetSpectras } from "../../api/queryHooks";
import { useState } from "react";
import TileCard from "../../components/shared/TileCard";
import ViewToggle from "../../components/shared/ViewToggle";
import DashPageHeader from "../../components/shared/DashPageHeader";

// ─── List row ────────────────────────────────────────────────────────────────
const SpectraDashRow = ({ space }) => {
  const user = UserAuthStore((state) => state.user);
  const navigate = useNavigate();
  const setShowSidebar = UIStore((state) => state.setShowSidebar);

  const handleClick = () => {
    navigate(`/spectra/${space.space_id}`);
    if (window.innerWidth < 940) setShowSidebar(false);
  };

  const isPersonal = space.collaborators.length === 1 && space.owner_id === user?.uid;
  const webSearchEnabled = space.web_search_enabled !== false;

  return (
    <div
      onClick={handleClick}
      className="photon-content-card flex items-center gap-3 px-4 py-3 rounded-xl bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-panel-border cursor-pointer hover:border-edge-primary dark:hover:border-edge-primary transition-colors"
    >
      {/* Avatar */}
      <div
        className="photon-avatar text-sm font-semibold w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0"
      >
        {space?.emoji ? (
          <span className="text-base">{space.emoji.emoji}</span>
        ) : (
          <span className="mt-[1px]">{getInitials(space.title)?.toUpperCase()}</span>
        )}
      </div>
      {/* Title + tag */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-ink-ink dark:text-ink-white truncate">{space.title}</span>
          {space.tag && (
            <span className="photon-accent-label photon-reference-tag inline-block max-w-full truncate px-2 py-0.5 rounded text-[10px] font-medium bg-fill-indigo-100 text-ink-indigo-800 dark:bg-fill-hex-312e81/60 dark:text-ink-hex-a5b4fc">
              #{space.tag.replace(/^#+/, "")}
            </span>
          )}
        </div>
        {space.description && (
          <p className="text-[11px] text-ink-gray-500 dark:text-ink-gray-400 truncate mt-0.5">{space.description}</p>
        )}
      </div>
      {/* Web search indicator + Collaborators or Personal */}
      <div className="flex items-center gap-2.5 flex-shrink-0">
        <span
          title={webSearchEnabled ? "Web search enabled" : "Web search disabled"}
          aria-label={webSearchEnabled ? "Web search enabled" : "Web search disabled"}
          className="inline-flex items-center"
        >
          <Globe
            className={`w-3.5 h-3.5 ${
              webSearchEnabled
                ? "text-ink-primary dark:text-ink-accent"
                : "text-ink-control-border dark:text-ink-gray-400 opacity-60"
            }`}
          />
        </span>
        {isPersonal ? (
          <span className="text-[10px] text-ink-gray-400">Personal</span>
        ) : (
          <SpectraUsersSharedStack space={space} />
        )}
      </div>
    </div>
  );
};

const SpectraDashCard = ({ space }) => {
  const user = UserAuthStore((state) => state.user);
  const navigate = useNavigate();
  const setShowSidebar = UIStore((state) => state.setShowSidebar);

  const handleClick = () => {
    navigate(`/spectra/${space.space_id}`);
    if (window.innerWidth < 940) setShowSidebar(false);
  };

  const avatar = (
    <div
      className="photon-avatar text-sm font-semibold w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0"
    >
      {space?.emoji ? (
        <span className="text-xl">{space.emoji.emoji}</span>
      ) : (
        <span className="mt-[2px]">{getInitials(space.title)?.toUpperCase()}</span>
      )}
    </div>
  );

  const backContent = (
    <div className="flex flex-col gap-1.5 pr-6">
      <h4 className="text-xs font-semibold text-ink-ink dark:text-ink-white mb-1">Space Details</h4>
      <p className="text-[10px] text-ink-gray-500 dark:text-ink-gray-400">
        <span className="font-medium text-ink-gray-700 dark:text-ink-gray-300">Created: </span>
        {timeConverter(new Date(space?.created_at))}
      </p>
      {space.collaborators.length === 1 && space.owner_id === user?.uid ? (
        <p className="text-[10px] text-ink-gray-500 dark:text-ink-gray-400">
          <span className="font-medium text-ink-gray-700 dark:text-ink-gray-300">Visibility: </span>
          Personal
        </p>
      ) : (
        <div className="mt-1">
          <p className="text-[10px] font-medium text-ink-gray-700 dark:text-ink-gray-300 mb-1">Collaborators</p>
          <SpectraUsersSharedStack space={space} />
        </div>
      )}
    </div>
  );

  const webSearchEnabled = space.web_search_enabled !== false;

  const statusBadge = (
    <span
      title={webSearchEnabled ? "Web search enabled" : "Web search disabled"}
      aria-label={webSearchEnabled ? "Web search enabled" : "Web search disabled"}
      className="inline-flex items-center p-1"
    >
      <Globe
        className={`w-3.5 h-3.5 ${
          webSearchEnabled
            ? "text-ink-primary dark:text-ink-accent"
            : "text-ink-control-border dark:text-ink-gray-400 opacity-60"
        }`}
      />
    </span>
  );

  return (
    <TileCard
      avatar={avatar}
      title={space.title}
      tag={space.tag ? `#${space.tag.replace(/^#+/, "")}` : undefined}
      description={space.description || undefined}
      status={statusBadge}
      onCardClick={handleClick}
      backContent={backContent}
    />
  );
};

const SpectraListing = ({ viewMode }) => {
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const { data: spectrasData } = useGetSpectras(orgId, user?.uid);
  const spaces = spectrasData || [];

  if (viewMode === "list") {
    return spaces.map((space) => <SpectraDashRow key={space.space_id} space={space} />);
  }
  return spaces.map((space) => <SpectraDashCard key={space.space_id} space={space} />);
};

const SpectraDash = () => {
  const [viewMode, setViewMode] = useState("grid");
  const navigate = useNavigate();
  const setShowSidebar = UIStore((state) => state.setShowSidebar);

  const handleCreate = () => {
    navigate(ROUTES.SPECTRA.CREATE);
    if (window.innerWidth < 940) setShowSidebar(false);
  };

  return (
    <div className="w-full">
      <Header type="spectra-dashboard" />
      <div className="h-[calc(100%-80px)] overflow-auto show-scrollbar">
        <div className="max-w-4xl px-6 mx-auto">
          <DashPageHeader
            title="Team Collaboration"
            description="Shared workspaces where teams collaborate with AI on projects. Each Spectra has a unique #tag that acts as a reusable skill—reference it from any conversation to access its shared knowledge, instructions, and files. Perfect for maintaining consistent team context across all AI interactions."
            action={{ label: "Spectra", onClick: handleCreate }}
          />

          {/* View toggle */}
          <div className="flex justify-end mb-4">
            <ViewToggle value={viewMode} onChange={setViewMode} />
          </div>

          {viewMode === "grid" ? (
            <div className="mt-2 gap-[25px] grid grid-cols-1 xm2:grid-cols-2 md:grid-cols-3">
              <SpectraListing viewMode="grid" />
            </div>
          ) : (
            <div className="mt-2 space-y-2">
              <SpectraListing viewMode="list" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpectraDash;
